import React, { useState } from 'react';
import { Box, Flex, Heading, Text } from '@chakra-ui/react';
import { Competitor, TennisGamePhase, TennisScore } from '../../types/types';
import {
  breaksInSet, gameLog, isTiebreak, latestScore, nextServer, PairOfCounts, playerPair, pointCalls,
  pointSituation, setsToWin,
} from '../../utils/tennisScore';
import { boardPlayers, BoardPlayer, boardSets, minutesSeconds, pairText, setScore } from './tennisBoard';
import TennisMatchStatus, { MatchState } from './TennisMatchStatus';
import TennisGameStrip, { GameSelection } from './TennisGameStrip';
import TennisGamePoints from './TennisGamePoints';

interface TennisScoreSequenceProps {
  tennisScores: TennisScore[];
  firstCompetitor: Competitor;
  secondCompetitor: Competitor;
  /** The competition's rule-set code: how many sets win the match. */
  ruleSet?: string;
}

const MATCH_STATES: Record<TennisGamePhase, MatchState> = {
  PREMATCH: 'PREMATCH',
  SET_IN_PROGRESS: 'LIVE',
  MATCH_END: 'FINISHED',
  CANCELLED: 'CANCELLED',
};

const breaksText = (breaks: PairOfCounts, players: [BoardPlayer, BoardPlayer]) => {
  const lead = breaks.first - breaks.second;
  if (lead === 0) return 'On serve';
  const ahead = lead > 0 ? players[0] : players[1];
  const count = Math.abs(lead);
  return `${ahead.competitor.name} ${count} break${count > 1 ? 's' : ''} up`;
};

/**
 * The tennis match board below the header's score: the state line, the games of each set with breaks of
 * serve, and one game point by point. Everything is rebuilt from the stored points;
 * the game log stops at the first point that does not follow the previous one and says so.
 */
export const TennisScoreSequence: React.FC<TennisScoreSequenceProps> = ({
  tennisScores,
  firstCompetitor,
  secondCompetitor,
  ruleSet
}) => {
  const [selection, setSelection] = useState<GameSelection | undefined>(undefined);
  const latest = latestScore(tennisScores);
  if (!latest) {
    return null;
  }

  const players = boardPlayers(firstCompetitor, secondCompetitor);
  const state = MATCH_STATES[latest.gamePhase];
  const pair = playerPair(latest, firstCompetitor.id, secondCompetitor.id);
  const pointRows = tennisScores.filter(s => s.sequence > 0);
  const lastPointAt = pointRows.length > 0 ? pointRows[pointRows.length - 1].timestamp : undefined;

  if (!pair || pointRows.length === 0) {
    return (
      <Box>
        <TennisMatchStatus state={state} where="No point played yet" tiebreak={false} players={players} />
      </Box>
    );
  }

  const matchSetsToWin = setsToWin(ruleSet);
  const log = gameLog(tennisScores, firstCompetitor.id, secondCompetitor.id, matchSetsToWin);
  const live = state === 'LIVE';
  const stopped = state === 'CANCELLED';
  const tiebreak = isTiebreak(pair.first.gamesWonCurrentSet, pair.second.gamesWonCurrentSet);
  const calls = pointCalls(pair.first.currentGamePoints, pair.second.currentGamePoints, tiebreak);
  const server = live ? nextServer(tennisScores, firstCompetitor.id, secondCompetitor.id) : undefined;
  const situation = live && server !== undefined ? pointSituation(pair, server, matchSetsToWin) : undefined;
  const setNo = pair.first.setsWon + pair.second.setsWon + 1;
  const gameNo = pair.first.gamesWonCurrentSet + pair.second.gamesWonCurrentSet + 1;

  const sets = boardSets(latest, log, firstCompetitor.id, secondCompetitor.id);

  let where: string;
  if (live) {
    where = tiebreak ? `Set ${setNo} · Tiebreak` : `Set ${setNo} · Game ${gameNo}`;
  } else if (stopped) {
    where = `Stopped in set ${setNo}, game ${gameNo}, at ${pairText(calls)}`;
  } else {
    const winner = pair.first.setsWon > pair.second.setsWon ? players[0] : players[1];
    where = `${winner.competitor.name} won ${sets.map(set => setScore(set, players)).join(' ')}`;
  }

  const inSet = live || stopped;
  const showCurrent = inSet && log.complete;
  const setsSeen = Array.from(new Set(log.games.map(g => g.set)));
  if (showCurrent && !setsSeen.includes(setNo)) {
    setsSeen.push(setNo);
  }
  const lastGameIndex = log.games.length - 1;
  const selected: GameSelection | undefined = selection ?? (showCurrent ? 'current' : lastGameIndex >= 0 ? lastGameIndex : undefined);
  const selectedGame = typeof selected === 'number' ? log.games[selected] : undefined;
  const firstPointAt = pointRows[0].timestamp;

  return (
    <Box>
      <TennisMatchStatus
        state={state}
        where={where}
        tiebreak={tiebreak}
        situation={situation}
        players={players}
        serverId={server}
        lastPointAt={lastPointAt}
      />
      <Flex gap={4} fontSize="sm" color="gray.600" flexWrap="wrap">
        {live && (
          <Text><Text as="span" fontWeight="semibold" color="gray.800">
            {breaksText(breaksInSet(log, setNo, firstCompetitor.id), players)}
          </Text> in set {setNo}</Text>
        )}
        {lastPointAt !== undefined && (
          <Text>Match time <Text as="span" fontWeight="semibold" color="gray.800">{minutesSeconds(lastPointAt - firstPointAt)}</Text></Text>
        )}
      </Flex>

      <Heading size="xs" mt={4} mb={2}>Games</Heading>
      {setsSeen.map(set => {
        const finished = sets.find(s => s.set === set);
        const winner = players.find(p => p.competitor.id === finished?.winnerCompetitorId);
        return (
          <TennisGameStrip
            key={set}
            set={set}
            result={finished ? `${setScore(finished, players)} · ${winner?.competitor.name ?? ''}` : stopped ? 'not finished' : 'in play'}
            players={players}
            games={log.games.map((game, index) => ({ game, index })).filter(({ game }) => game.set === set)}
            current={showCurrent && set === setNo ? { points: log.current, calls, serverId: server, stopped } : undefined}
            selected={selected}
            onSelect={setSelection}
          />
        );
      })}
      {!log.complete && (
        <Text fontSize="xs" color="orange.600" mb={2}>
          The stored points do not follow one another here, so the games after this point are not shown.
          The scoreboard above is the latest state and is not affected.
        </Text>
      )}

      <TennisGamePoints
        players={players}
        game={selectedGame}
        current={selected === 'current' && showCurrent ? {
          points: log.current,
          label: tiebreak ? 'Tiebreak' : `Game ${gameNo}`,
          serverId: server,
          stopped,
        } : undefined}
      />

    </Box>
  );
};

export default TennisScoreSequence;
